"use client";

import { useState, useCallback, useMemo, useEffect } from "react";
import { AssetCategory, SelectedAssetIds } from "./types";

/**
 * useWatchlistAddAssets Hook 的返回值类型
 */
export interface UseWatchlistAddAssetsReturn {
  /** 当前按分类划分的选中资产 */
  selectedByCategory: SelectedAssetIds;
  /** 合并后的所有选中资产 ID 数组 */
  selectedIds: string[];
  /** 选中数量 */
  selectedCount: number;
  /** 处理选择变化 */
  handleSelectionChange: (
    category: AssetCategory,
    changedId: string,
    newSelectedIds: string[],
  ) => void;
}

/**
 * useWatchlistAddAssets Hook 的参数类型
 */
export interface UseWatchlistAddAssetsOptions {
  /** 初始选中的资产 ID （按分类） */
  initialSelectedIds?: Partial<SelectedAssetIds>;
  /** 选择变化回调 */
  onSelectionChange?: (selected: SelectedAssetIds) => void;
}

/**
 * useWatchlistAddAssets Hook
 * 管理资产选择状态和业务逻辑
 *
 * @param options Hook 配置选项
 * @returns UseWatchlistAddAssetsReturn
 */
export const useWatchlistAddAssets = (
  options: UseWatchlistAddAssetsOptions = {},
): UseWatchlistAddAssetsReturn => {
  const { initialSelectedIds, onSelectionChange } = options;

  const mergeInitialSelected = useCallback(
    (partial?: Partial<SelectedAssetIds>): SelectedAssetIds => ({
      stocks: partial?.stocks ? Array.from(new Set(partial.stocks)) : [],
      crypto: partial?.crypto ? Array.from(new Set(partial.crypto)) : [],
    }),
    [],
  );

  /**
   * 选择状态管理
   */
  const [selectedByCategory, setSelectedByCategory] =
    useState<SelectedAssetIds>(() => mergeInitialSelected(initialSelectedIds));

  useEffect(() => {
    setSelectedByCategory(mergeInitialSelected(initialSelectedIds));
  }, [initialSelectedIds, mergeInitialSelected]);

  const selectedIds = useMemo(
    () => [...selectedByCategory.stocks, ...selectedByCategory.crypto],
    [selectedByCategory],
  );

  /**
   * 计算选中数量
   */
  const selectedCount = useMemo(() => selectedIds.length, [selectedIds]);

  /**
   * 处理选择变化
   */
  const handleSelectionChange = useCallback(
    (category: AssetCategory, _changedId: string, newSelectedIds: string[]) => {
      setSelectedByCategory((prev) => {
        const next: SelectedAssetIds = {
          ...prev,
          [category]: Array.from(new Set(newSelectedIds)),
        };
        onSelectionChange?.(next);
        return next;
      });
    },
    [onSelectionChange],
  );

  return {
    selectedByCategory,
    selectedIds,
    selectedCount,
    handleSelectionChange,
  };
};
